import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'xp-input',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => XpInputComponent),
      multi: true,
    },
  ],
  template: `
    <div class="base-input">
      <input
        #tooltipEl="matTooltip"
        [attr.id]="id"
        [attr.type]="type"
        [matTooltip]="tooltip"
        [matTooltipPosition]="tooltipPosition"
        [matTooltipClass]="state + ' ' + tooltipPosition"
        [attr.name]="name"
        [attr.placeholder]="placeholder"
        [(ngModel)]="ngModel"
        #ctrl="ngModel"
        [className]="state + ' ' + 'base-input-control'"
        (input)="onChange(ngModel)"
        (focus)="onFocusInternal()"
        (blur)="blur.emit()"
        [disabled]="disable"
        [readOnly]="state === 'readonly' || state === 'button' || readOnly"
        [matAutocomplete]="auto"
        #inputRef
      />

      <mat-autocomplete #auto="matAutocomplete">
        <cdk-virtual-scroll-viewport class="autocomplete-test-viewport" [ngStyle]="{ height: height }" itemSize="25">
          <mat-option
            *cdkVirtualFor="let option of filteredOptions"
            [value]="option"
            class="autocomplete-item"
            (onSelectionChange)="onOptionClick($event)"
          >
            {{ option }}
          </mat-option>
        </cdk-virtual-scroll-viewport>
      </mat-autocomplete>
    </div>
  `,
})
export class XpInputComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() ngModel: any;
  @Output() ngModelChange = new EventEmitter<any>();
  @Input() state: string = '';
  @Input() name: string = '';
  @Input() id: string = String(Date.now());
  @Input() placeholder = '';
  @Input() tooltip: string;
  @Input() disable: boolean;
  @Input() autoCompleteOptions: string[];
  @Input() icon: string;
  @Input() type = 'text';
  @Input() readOnly = false;
  @Input() isFocused = false;
  @Output() iconFn = new EventEmitter<any>();
  @Output() inputChange = new EventEmitter<any>();
  @Output() focus = new EventEmitter<any>();
  @Output() blur = new EventEmitter<any>();

  @ViewChild('tooltipEl') tooltipRef: MatTooltip;
  @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;

  tooltipPosition: TooltipPosition = 'above';
  firstChange = true;
  propagateChange = (_: any) => {};
  propagateTouch = () => {};
  initialChange = true;
  filteredOptions = [];
  height: string;

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.filteredOptions = (this.autoCompleteOptions || []).filter((option) =>
      (option || '').toLowerCase().startsWith((this.ngModel || '').toLowerCase()),
    );

    // Recompute how big the viewport should be.
    if (this.filteredOptions.length < 4) {
      this.height = `${this.filteredOptions.length * 50}px`;
    } else {
      this.height = '200px';
    }

    if (this.ngModel !== undefined) {
      this.onChange(this.ngModel);
    }

    if (this.disable) {
      this.elementRef.nativeElement.classList.add('disabled');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tooltip && !changes.tooltip.previousValue && changes.tooltip.currentValue && this.tooltipRef) {
      this.tooltipRef.message = changes.tooltip.currentValue;
      this.tooltipRef.show();
    }

    if (changes.isFocused && changes.isFocused.currentValue && !changes.isFocused.previousValue) {
      setTimeout(() => {
        this.inputRef.nativeElement.focus();
      });
    }

    if (
      changes.ngModel &&
      changes.ngModel?.currentValue &&
      !changes.ngModel?.previousValue &&
      this.initialChange &&
      this.inputRef
    ) {
      this.inputRef.nativeElement.value = changes.ngModel.currentValue;
      this.inputRef.nativeElement.dispatchEvent(
        new Event('input', {
          bubbles: true,
          cancelable: true,
        }),
      );
      this.initialChange = false;
    }

    if (this.disable) {
      this.elementRef.nativeElement.classList.add('disabled');
    } else {
      this.elementRef.nativeElement.classList.remove('disabled');
    }
  }

  onChange(value) {
    this.filteredOptions = (this.autoCompleteOptions || []).filter((option) =>
      (option || '').toLowerCase().startsWith((value || '').toLowerCase()),
    );

    // Recompute how big the viewport should be.
    if (this.filteredOptions.length < 4) {
      this.height = `${this.filteredOptions.length * 50}px`;
    } else {
      this.height = '200px';
    }

    this.ngModelChange.emit(value);
    this.propagateChange(value);

    if (this.inputChange) {
      this.inputChange.emit({ value, isFirstChange: this.firstChange });
    }

    this.firstChange = false;
  }

  onFocusInternal() {
    this.focus.emit();
    this.propagateTouch();
  }

  onOptionClick(event: any) {
    this.onChange(event.source.value);
  }

  writeValue(value: any) {
    if (value !== undefined) {
      this.ngModel = value;
    }
  }

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn) {
    this.propagateTouch = fn;
  }
}

((window as any).xplenty as angular.IModule).directive(
  'xpInput',
  downgradeComponent({
    component: XpInputComponent,
  }),
);
